import {
  type ClientSearchItem,
  EQUIPMENT_LIFECYCLE_LABELS,
  type EquipmentHistoryItem,
  type EquipmentLifecycle,
  type EquipmentListItem,
  type SlaState,
  TICKET_PRIORITY_LABELS,
  TICKET_STATUS_LABELS,
  type TicketListItem,
  type TicketPriority,
  type TicketStatus,
} from '@erp/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import { AlertTriangle, Clock } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useCan } from '@/lib/auth';
import { fetchClients, fetchEquipment, helpdeskCountersQuery } from '@/lib/equipment';
import { formatDay, formatMoment } from '@/lib/format';
import { SearchPicker } from './crm-bits';
import { Avatar, Badge } from './ui';

/** Вкладки раздела «Оборудование»: производство, база, заявки, настройки. */
export function EquipmentNav() {
  const can = useCan();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const counters = useQuery({
    ...helpdeskCountersQuery,
    enabled: can('helpdesk.access') || can('helpdesk.ticket.create'),
  });
  const attention = (counters.data?.mine ?? 0) + (counters.data?.free ?? 0);
  const tabs: { to: string; label: string; show: boolean; badge?: number; alert?: boolean }[] = [
    { to: '/production', label: 'Производство', show: can('production.manage') },
    {
      to: '/equipment',
      label: 'База оборудования',
      show: can('equipment.read') || can('helpdesk.access') || can('production.manage'),
    },
    {
      to: '/helpdesk',
      label: 'Сервисные заявки',
      show: can('helpdesk.access') || can('helpdesk.ticket.create') || can('equipment.read'),
      badge: attention || undefined,
      alert: Boolean(counters.data?.overdue),
    },
    { to: '/equipment/settings', label: 'Настройки', show: can('equipment.settings.manage') },
  ];
  const active = (to: string) =>
    to === '/equipment'
      ? pathname === '/equipment' ||
        (pathname.startsWith('/equipment/') && !pathname.startsWith('/equipment/settings'))
      : pathname === to || pathname.startsWith(`${to}/`);
  return (
    <nav
      aria-label="Разделы оборудования"
      className="-mx-4 mb-5 overflow-x-auto border-b border-line px-4 lg:-mx-8 lg:px-8"
    >
      <ul className="flex gap-1">
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <li key={t.to}>
              <Link
                to={t.to}
                aria-current={active(t.to) ? 'page' : undefined}
                className={clsx(
                  'relative inline-flex h-10 items-center gap-1.5 whitespace-nowrap px-3 text-[14px]',
                  active(t.to) ? 'font-semibold text-ink' : 'text-steel hover:text-ink',
                )}
              >
                {t.label}
                {t.badge ? (
                  <span
                    className={clsx(
                      'min-w-[18px] rounded-full px-1.5 text-center text-[11px] font-semibold leading-[17px] tabular-nums',
                      t.alert ? 'bg-danger text-white' : 'bg-sunken text-ink-soft',
                    )}
                  >
                    {t.badge}
                  </span>
                ) : null}
                {active(t.to) && (
                  <span aria-hidden className="absolute inset-x-2 -bottom-px h-[3px] rounded-t bg-signal" />
                )}
              </Link>
            </li>
          ))}
      </ul>
    </nav>
  );
}

export function LifecycleBadge({ lifecycle }: { lifecycle: EquipmentLifecycle }) {
  const tone = lifecycle === 'in_service' ? 'ok' : lifecycle === 'decommissioned' ? 'neutral' : 'warn';
  return <Badge tone={tone}>{EQUIPMENT_LIFECYCLE_LABELS[lifecycle]}</Badge>;
}

export function PriorityBadge({ priority }: { priority: TicketPriority }) {
  const tone = priority === 'critical' ? 'danger' : priority === 'high' ? 'warn' : 'neutral';
  return <Badge tone={tone}>{TICKET_PRIORITY_LABELS[priority]}</Badge>;
}

export function TicketStatusBadge({ status }: { status: TicketStatus }) {
  const styles: Record<TicketStatus, string> = {
    new: 'bg-[#e3eef8] text-[#1f5d99]',
    in_progress: 'bg-[#e6f1ef] text-[#20766f]',
    waiting: 'bg-sunken text-ink-soft',
    done: 'bg-ok-soft text-ok',
    cancelled: 'bg-sunken text-steel line-through',
  };
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded px-1.5 py-0.5 text-[12px] font-medium',
        styles[status],
      )}
    >
      {TICKET_STATUS_LABELS[status]}
    </span>
  );
}

/** Срок заявки: «до 14:00», «просрочена реакция», «решена в срок». */
export function SlaMark({
  ticket,
  long,
}: {
  ticket: Pick<TicketListItem, 'sla' | 'resolveDueAt' | 'reactionDueAt' | 'reactedAt'>;
  long?: boolean;
}) {
  const s: SlaState = ticket.sla;
  if (s === 'none') return null;
  if (s === 'met') return <span className="text-[12.5px] text-ok">в срок</span>;
  if (s === 'breached') return <span className="text-[12.5px] text-danger">с опозданием</span>;
  if (s === 'reaction_overdue' || s === 'resolve_overdue')
    return (
      <span className="inline-flex items-center gap-1 text-[12.5px] font-medium text-danger">
        <AlertTriangle className="size-3.5" aria-hidden />
        {s === 'reaction_overdue' ? 'не взята вовремя' : 'просрочена'}
      </span>
    );
  const due = ticket.reactedAt ? ticket.resolveDueAt : ticket.reactionDueAt;
  return (
    <span
      className="inline-flex items-center gap-1 text-[12.5px] text-steel"
      title={ticket.reactedAt ? 'Срок решения' : 'Срок реакции'}
    >
      <Clock className="size-3.5" aria-hidden />
      {long ? (ticket.reactedAt ? 'решить до ' : 'взять до ') : 'до '}
      {formatMoment(due)}
    </span>
  );
}

/** Выбор клиента (контакт или компания) — поиск на сервере без открытия всей CRM. */
export function ClientPicker({
  value,
  onChange,
  id,
  invalid,
}: {
  value: { id: string; label: string } | null;
  onChange: (c: ClientSearchItem | null) => void;
  id?: string;
  invalid?: boolean;
}) {
  return (
    <SearchPicker<ClientSearchItem>
      id={id}
      invalid={invalid}
      value={value}
      onChange={onChange}
      fetchItems={fetchClients}
      cacheKey="equipment-clients"
      placeholder="Имя, компания, телефон или ИНН"
      renderItem={(c) => ({
        title: c.title,
        hint: c.kind === 'company' ? ['Компания', c.hint].filter(Boolean).join(' · ') : (c.hint ?? undefined),
      })}
    />
  );
}

export function EquipmentPicker({
  value,
  onChange,
  id,
}: {
  value: { id: string; label: string } | null;
  onChange: (e: EquipmentListItem | null) => void;
  id?: string;
}) {
  return (
    <SearchPicker<EquipmentListItem>
      id={id}
      value={value}
      onChange={onChange}
      fetchItems={fetchEquipment}
      cacheKey="equipment"
      placeholder="Заводской №, плата, модель или клиент"
      renderItem={(e) => ({
        title: e.title,
        hint: [e.contact?.fullName ?? e.company?.name, EQUIPMENT_LIFECYCLE_LABELS[e.lifecycle]]
          .filter(Boolean)
          .join(' · '),
      })}
    />
  );
}

export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[12.5px] text-steel">{label}</dt>
      <dd className="mt-0.5 min-w-0 break-words">{children}</dd>
    </div>
  );
}

export function Person({
  user,
  empty = 'не назначен',
}: {
  user: { fullName: string } | null;
  empty?: string;
}) {
  if (!user) return <span className="text-steel">{empty}</span>;
  return (
    <span className="flex items-center gap-2">
      <Avatar name={user.fullName} size={22} />
      {user.fullName}
    </span>
  );
}

/** Ожидание комплектующих: дата красная, если прошла, а стенд ещё не собран. */
export function ExpectedDate({
  label,
  date,
  assembled,
}: {
  label: string;
  date: string | null;
  assembled: boolean;
}) {
  if (!date) return null;
  const today = new Date().toISOString().slice(0, 10);
  const late = !assembled && date < today;
  return (
    <span className={clsx('whitespace-nowrap', late ? 'font-medium text-danger' : 'text-steel')}>
      {label}: {formatDay(date).replace(/ \d{4}( г\.)?$/, '')}
    </span>
  );
}

const ACTION_LABELS: Record<string, string> = {
  created: 'запустил в производство',
  registered: 'внёс в базу',
  updated: 'изменил',
  stage: 'перенёс',
  shipped: 'отгрузил клиенту',
  decommissioned: 'списал',
  ticket: 'создал заявку',
  taken: 'взял в работу',
  status: 'сменил статус',
  deleted: 'удалил',
};

const FIELD_LABELS: Record<string, string> = {
  modelId: 'модель',
  name: 'название',
  serialNo: 'заводской №',
  boardId: 'ID платы',
  assemblerId: 'сборщик',
  caseExpectedAt: 'ожидание корпуса',
  boardExpectedAt: 'ожидание платы',
  assembledAt: 'окончание сборки',
  shippedAt: 'дата отгрузки',
  contactId: 'контакт',
  companyId: 'компания',
  dealId: 'сделка',
  inventoryNo: 'инв. №',
  location: 'место',
  warrantyUntil: 'гарантия',
  contractFileId: 'договор',
  notes: 'примечание',
  subject: 'тема',
  description: 'описание',
  categoryId: 'категория',
  priority: 'приоритет',
  equipmentId: 'оборудование',
  source: 'источник',
  assigneeId: 'исполнитель',
};

function describe(h: EquipmentHistoryItem) {
  const d = h.diff ?? {};
  if (h.action === 'stage') return `${d.stage?.from ?? '—'} → ${d.stage?.to}`;
  if (h.action === 'ticket') return String(d.ticket?.to ?? '');
  if (h.action === 'decommissioned') return String(d.reason?.to ?? '');
  if (h.action === 'status') {
    const to = d.status?.to as TicketStatus | undefined;
    return `${to ? TICKET_STATUS_LABELS[to] : ''}${d.resolution?.to ? ` — ${d.resolution.to}` : ''}`;
  }
  if (h.action === 'updated' || h.action === 'shipped')
    return Object.keys(d)
      .map((k) => FIELD_LABELS[k])
      .filter(Boolean)
      .join(', ');
  return '';
}

export function HistoryPanel({ items }: { items: EquipmentHistoryItem[] | undefined }) {
  const [all, setAll] = useState(false);
  if (!items?.length) return null;
  const shown = all ? items : items.slice(0, 8);
  return (
    <section className="panel px-5 py-4">
      <h2 className="mb-2 font-semibold">История</h2>
      <ol className="flex flex-col gap-2.5 text-[13px]">
        {shown.map((h) => (
          <li key={h.id}>
            <span className="text-steel">{formatMoment(h.at)}</span>
            <span className="block">
              <span className="font-medium">{h.actor?.fullName ?? 'Система'}</span>{' '}
              {ACTION_LABELS[h.action] ?? h.action} <span className="text-ink-soft">{describe(h)}</span>
            </span>
          </li>
        ))}
      </ol>
      {items.length > 8 && (
        <button type="button" className="link mt-2 text-[13px]" onClick={() => setAll(!all)}>
          {all ? 'Свернуть' : `Вся история (${items.length})`}
        </button>
      )}
    </section>
  );
}
